import { useEffect, useRef, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import useCurrentUser from "~/hooks/user/useCurrentUser";
import { clearAllCollectionCache } from "~/lib/cache/clearCache";
import createKey from "~/lib/cache/createKey";
import {
  bulkDeleteCollectionItemsById,
  bulkUpdateCollectionItemsByIds,
  createCollectionFileItem,
  createCollectionItem,
  deleteCollectionItemById,
  getCollectionItemById,
  updateCollectionFileItemById,
  updateCollectionItemById,
} from "~/lib/payload/collection";
import type { CollectionNames } from "~/types";
import type { ApiResponse } from "~/types/apiAndHookBase";
import type { CollectionItem } from "~/types/collections";

// TO NOTE
// WHEN ADDING A NEW ITEM, IT WILL ONLY UPDATE THE -all- CACHE WHEN THE CORRECT DEPTH CACHE KEY
// IS TARGETTED. IF NO DEPTH HAS BEEN USED THEN IT WILL "JUST WORK". IF A DEPTH HAS BEEN USED
// THEN YOU WILL NEED TO FIND AND PASS INTO THE UPDATE CALL.

/**
 * Interface for the parameters of the useData function.
 */
interface UseDataParams {
  collection: CollectionNames;
  id?: string | null | undefined;
  depth?: number;
  keyAddition?: string;
  serialLoading?: boolean;
}

export interface UseDataReturn<TInput = any, TOutput = any> {
  data: TOutput | undefined;
  isLoading: boolean;
  createItem: (item: Partial<TInput>) => Promise<ApiResponse<TOutput>>;
  updateItem: (
    arg: Partial<TOutput>,
    idOverride?: string
  ) => Promise<ApiResponse<TOutput>>;
  deleteItem: (options?: {
    revalidationCollection?: boolean;
  }) => Promise<boolean | null>;
  refetch: () => void;
  isUpdating: boolean;
  isDeleted: boolean;
}

/**
 * This is the base hook for fetching data that requires authentication.
 * It uses the useCurrentUser hook to check if a user is logged in before fetching the requested data.
 * @param {UseDataParams} params - The parameters for the hook.
 * @returns {UseDataReturn} The data and functions for the hook.
 */
export default function useData<
  TInput,
  TOutput extends CollectionItem = CollectionItem,
>({
  collection,
  id,
  depth,
  keyAddition,
  serialLoading,
}: UseDataParams): UseDataReturn<TInput, TOutput> {
  const previousIdRef = useRef(id);
  const queryClient = useQueryClient();

  const [updating, setUpdating] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const {
    data: { id: userId, organization },
    isLoading: userLoading,
  } = useCurrentUser();

  const organizationId = organization?.id;

  const runQuery = Boolean(
    !serialLoading && !userLoading && userId && collection && id && !isDeleted
  );

  const key = createKey({ userId, collection, id, depth, keyAddition });

  // Get data and cache it.
  const { data, isLoading, refetch } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [key],
    queryFn: () => getCollectionItemById({ collection, id: id || "", depth }),
    enabled: runQuery,
    refetchOnWindowFocus: false,
  });

  // Reset isDeleted state when id changes
  useEffect(() => {
    if (id !== previousIdRef.current) {
      // Reset the deleted state when switching to a new item
      setIsDeleted(false);
      previousIdRef.current = id;
    }
  }, [id]);

  const createItem = async (
    item: Partial<TInput>
  ): Promise<ApiResponse<TOutput>> => {
    let res: ApiResponse<TOutput>;

    if (collection) {
      const dataToSave = item as TInput & { organization: string };

      if (!(item as any)?.organization) {
        dataToSave.organization = organizationId;
      }

      if ((item as any).file) {
        const { file, ...rest } = (item as any) || {};

        const payload = rest;

        res = await createCollectionFileItem<TOutput>({
          collection,
          file,
          data: payload,
          signal: (item as any)?.signal,
        });
      } else {
        res = await createCollectionItem<TOutput>({
          collection,
          data: dataToSave,
        });
      }

      if (res?.success) {
        // On creation success, clear the global collection cache.
        // TODO - would be better to mutate the cache instead of clearing it.
        clearAllCollectionCache(collection);

        // Handle joined item type.
        if (collection === "contacts" || collection === "tenants") {
          clearAllCollectionCache("contacts-and-tenants");
        }
      }
      return res;
    }

    return {
      success: false,
      message: "Collection not specified",
    };
  };

  // Update data with mutation.
  const updateItem = async (
    arg: Partial<TOutput>,
    idOverride?: string
  ): Promise<ApiResponse<TOutput>> => {
    if (!userLoading && collection && (id || idOverride)) {
      setUpdating(true);

      let res: ApiResponse<TOutput>;

      if ((arg as any).file) {
        const { file, ...rest } = (arg as any) || {};

        const payload = rest;

        res = await updateCollectionFileItemById({
          collection,
          id: idOverride || id || "",
          file,
          data: payload,
          signal: (arg as any)?.signal,
        });
      } else {
        res = await updateCollectionItemById({
          collection,
          id: id || idOverride || "",
          data: arg,
        });
      }

      if (res?.success) {
        clearAllCollectionCache(collection);
      }

      setUpdating(false);

      return res;
    }

    return {
      success: false,
      message: "Collection or ID not specified",
    };
  };

  const deleteItem = async (options?: { revalidationCollection?: boolean }) => {
    const { revalidationCollection = true } = options || {};

    if (!userLoading && collection && id) {
      const res = await deleteCollectionItemById({ collection, id });

      if (res.success) {
        // Mark the item as deleted locally to prevent further fetches
        setIsDeleted(true);

        // Remove this specific item from cache
        queryClient.removeQueries({ queryKey: [key] });

        if (revalidationCollection) {
          // Clear the collection cache for other components
          clearAllCollectionCache(collection);
        }

        return true;
      } else {
        return false;
      }
    }

    return null;
  };

  const refetchQuery = () => {
    if (!isDeleted) {
      refetch();
    }
  };

  return {
    createItem,
    data: data?.data as TOutput | undefined,
    deleteItem,
    isLoading: Boolean(isLoading || userLoading || serialLoading),
    updateItem,
    refetch: refetchQuery,
    isUpdating: updating,
    isDeleted,
  };
}

// This allows us to create an item, without importing/loading the whole useData hook.
export const useCreateItem = <TInput = any, TOutput = any>({
  collection,
}: {
  collection: CollectionNames;
}) => {
  const {
    data: { organization },
  } = useCurrentUser();

  const organizationId = organization?.id;

  const createItem = async (item: TInput): Promise<ApiResponse<TOutput>> => {
    let res: ApiResponse<TOutput>;

    if (collection) {
      const dataToSave = item as any;

      if (!(item as any)?.organization) {
        dataToSave.organization = organizationId;
      }

      // Check if the item contains a file for upload
      if ((item as any).file) {
        const { file, ...rest } = dataToSave || {};

        const payload = rest;

        if (!(item as any)?.organization) {
          payload.organization = organizationId;
        }

        res = await createCollectionFileItem({
          collection,
          file,
          data: payload,
          signal: (item as any)?.signal,
        });
      } else {
        const dataToSave = item;

        res = await createCollectionItem<TOutput>({
          collection,
          data: dataToSave as unknown as Record<string, unknown>,
        });
      }

      if (res.success) {
        // On creation success, clear the global collection cache.
        // TODO - would be better to mutate the cache instead of clearing it.
        clearAllCollectionCache(collection);

        // Handle joined item type.
        if (collection === "contacts" || collection === "tenants") {
          clearAllCollectionCache("contacts-and-tenants");
        }
      }
      return res;
    }

    return {
      success: false,
      message: "Collection not specified",
    } as ApiResponse<TOutput>;
  };

  return createItem;
};

// This allows us to delete an item, without importing/loading the whole useData hook.
// or calling the collection endpoint with the ID first...
export const useDeleteItem = ({
  collection,
}: {
  collection: CollectionNames;
}) => {
  const deleteItem = async (
    id: string,
    clearCollection: boolean = true
  ): Promise<boolean | null> => {
    if (collection && id) {
      const res = await deleteCollectionItemById({ collection, id });

      if (res.success) {
        if (clearCollection) {
          // On deletion success, clear the global collection cache.
          clearAllCollectionCache(collection);
        }

        return true;
      } else {
        return false;
      }
    }

    return null;
  };

  return deleteItem;
};

export const useBulkDeleteItems = ({
  collection,
}: {
  collection: CollectionNames;
}) => {
  const bulkDeleteItems = async (ids: string[]): Promise<boolean | null> => {
    if (collection && ids) {
      const res = await bulkDeleteCollectionItemsById({ collection, ids });

      if (res.success) {
        // On deletion success, clear the global collection cache.
        clearAllCollectionCache(collection);

        return true;
      } else {
        return false;
      }
    }

    return null;
  };

  return bulkDeleteItems;
};

// This allows us to update an item, without importing/loading the whole useData hook.
// or calling the collection endpoint with the ID first...
export const useUpdateItem = ({
  collection,
}: {
  collection: CollectionNames;
}) => {
  const updateItem = async ({
    id,
    arg,
  }: {
    id: string;
    arg: any;
  }): Promise<boolean | null> => {
    if (collection && id) {
      const res = await updateCollectionItemById({
        collection,
        id: id,
        data: arg,
      });

      if (res.success) {
        // On deletion success, clear the global collection cache.
        // TODO - would be better to mutate the cache instead of clearing it.
        // especially for the -all-data caches.
        clearAllCollectionCache(collection);

        return true;
      } else {
        return false;
      }
    }

    return null;
  };

  return updateItem;
};

export const useBulkUpdateItems = ({
  collection,
}: {
  collection: CollectionNames;
}) => {
  const bulkUpdateItems = async ({
    ids,
    arg,
  }: {
    ids: string[];
    arg: any;
  }): Promise<boolean | null> => {
    if (collection && ids) {
      const res = await bulkUpdateCollectionItemsByIds({
        collection,
        ids,
        data: arg,
      });

      if (res.success) {
        // On deletion success, clear the global collection cache.
        // TODO - would be better to mutate the cache instead of clearing it.
        // especially for the -all-data caches.
        clearAllCollectionCache(collection);

        return true;
      } else {
        return false;
      }
    }

    return null;
  };

  return bulkUpdateItems;
};
